import React, { useState } from "react";
import DataTable from "../../../../components/common/DataTable";
import DateRangePicker, { useDatePresetRange } from "../../../../components/common/DateRangePicker";
import ListExportToolbar from "../../../stock/components/list/ListExportToolbar";
import { useGetPettyCashSummaryQuery } from "../../pettyCashApi";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";

const money = (n) => Number(n || 0).toFixed(2);

const GROUP_OPTIONS = [
  { value: "category", label: "By Category" },
  { value: "branch", label: "By Branch" },
  { value: "fund", label: "By Fund" },
  { value: "month", label: "By Month" },
];

// Server-side aggregation (dbo.sp_app_pettycash_voucher_summary, Batch21) -
// one grouped-totals view over voucher spend, same shape as Stock's own
// grouped report pages.
const SummaryReportPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const { preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo } = useDatePresetRange("THIS_MONTH");
  const [groupBy, setGroupBy] = useState("category");

  const { data: rows = [], isLoading } = useGetPettyCashSummaryQuery({
    branchId: selectedBranchId ?? undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    groupBy,
  });

  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;
  const totalAmount = rows.reduce((s, r) => s + Number(r.totalAmount || 0), 0);
  const totalPaid = rows.reduce((s, r) => s + Number(r.paidAmount || 0), 0);
  // Returns (Batch168) are counted against their voucher's group - so "By
  // Category" shows what each category really cost once change came back.
  const totalReturned = rows.reduce((s, r) => s + Number(r.returnedAmount || 0), 0);
  const netOf = (r) => Number(r.paidAmount || 0) - Number(r.returnedAmount || 0);

  const columns = [
    { title: GROUP_OPTIONS.find((g) => g.value === groupBy)?.label.replace("By ", "") || "Group", dataIndex: "groupLabel" },
    { title: "Vouchers", dataIndex: "totalCount" },
    { title: "Requested Amount", dataIndex: "totalAmount", render: (v) => money(v) },
    { title: "Paid", dataIndex: "paidCount" },
    { title: "Paid Amount", dataIndex: "paidAmount", render: (v) => money(v) },
    { title: "Returned", dataIndex: "returnedAmount", render: (v) => money(v) },
    { title: "Net Spend", key: "net", render: (_, r) => <strong>{money(netOf(r))}</strong> },
  ];

  const exportColumns = [
    { header: columns[0].title, value: (r) => r.groupLabel },
    { header: "Vouchers", value: (r) => r.totalCount },
    { header: "Requested Amount", value: (r) => money(r.totalAmount), align: "right" },
    { header: "Paid", value: (r) => r.paidCount },
    { header: "Paid Amount", value: (r) => money(r.paidAmount), align: "right" },
    { header: "Returned", value: (r) => money(r.returnedAmount), align: "right" },
    { header: "Net Spend", value: (r) => money(netOf(r)), align: "right" },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Petty Cash Summary Report</h4>
              <h6>Grouped voucher spend totals</h6>
            </div>
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-6 col-md">
            <div className="card"><div className="card-body py-2">
              <div className="text-muted small">Requested</div>
              <div className="fs-5 fw-bold">{money(totalAmount)}</div>
            </div></div>
          </div>
          <div className="col-6 col-md">
            <div className="card"><div className="card-body py-2">
              <div className="text-muted small">Paid</div>
              <div className="fs-5 fw-bold text-success">{money(totalPaid)}</div>
            </div></div>
          </div>
          <div className="col-6 col-md">
            <div className="card"><div className="card-body py-2">
              <div className="text-muted small">Returned</div>
              <div className="fs-5 fw-bold">{money(totalReturned)}</div>
            </div></div>
          </div>
          <div className="col-6 col-md">
            <div className="card"><div className="card-body py-2">
              <div className="text-muted small">Net Spend</div>
              <div className="fs-5 fw-bold">{money(totalPaid - totalReturned)}</div>
            </div></div>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex flex-column gap-2 mb-2">
              <DateRangePicker preset={preset} setPreset={setPreset} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <select className="form-control form-control-sm" style={{ width: 200 }} value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                  {GROUP_OPTIONS.map((g) => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </select>
                <ListExportToolbar
                  title="Petty Cash Summary Report"
                  subtitleLines={[`Branch: ${branchCode || "All"}`, `Date: ${dateFrom} to ${dateTo}`]}
                  branchCode={branchCode}
                  columns={exportColumns}
                  rows={rows}
                  fileName="PettyCashSummaryReport"
                />
              </div>
            </div>
            <div className="table-responsive">
              <DataTable columns={columns} dataSource={rows} rowKey={(r) => r.groupKey} loading={isLoading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryReportPage;
